import { FC, useMemo, useState } from 'react';
import {
  SideSheet,
  SideSheetSize,
  SideSheetClose,
  SideSheetHeader,
  ButtonSize,
  SideSheetBody,
  Box,
  Text,
  theme,
  IconSize,
  Icon,
  SideSheetFooter,
} from '@bechtel/gds';
import { t, Trans } from '@lingui/macro';
import { useName } from '@bechtel/client-common';
import { differenceWith, get, isEqual, map, sumBy, filter, capitalize, find } from 'lodash';
import { object, string, array } from 'yup';
import { Formik } from 'formik';
import { useTimeSheetReason } from '~/hooks';
import { FooterButtonStyled, AvatarRegularStyled } from '../add-comment-sidesheet';
import { CommentProps, ViewCommentSidesheetProps } from './types';
import { AddComment } from '~/components/molecules';
import { useTimesheet } from '~/detail/context';
import { TimeEntryInput } from '@bechtel/types';
import { CommentMessages } from '~/components/atoms';
import { TimesheetReasons } from '~/detail/types';

const TitleValue: React.FC<{ title: string; value: string }> = ({ title, value }) => {
  return (
    <Box style={{ lineHeight: '1.57' }}>
      <Text size="400" color="grey.1000" weight="600" family="graphikCompact">
        {title}:&nbsp;
      </Text>
      <Text size="400" color="grey.600" family="graphikCompact">
        {value}
      </Text>
    </Box>
  );
};

const Comment: React.FC<CommentProps> = ({ timesheetEntry, index, errors, setFieldValue, reasonList }) => {
  const [isEdit, setIsEdit] = useState(false);
  const { hourTypes } = useTimesheet();
  const hourDescription = useMemo(
    () => find(hourTypes, (hour) => hour.hourCode === timesheetEntry.hourCode)?.description || '',
    [timesheetEntry.hourCode]
  );
  const timesheetReason = useMemo(
    () => find(reasonList, (reason) => reason.id === timesheetEntry.commentId)?.name || '',
    [timesheetEntry.commentId]
  );
  const otherReasonId = find(reasonList, (reason) => reason.name == TimesheetReasons.TIMESHEET_REASON_OTHER)?.id;

  return (
    <Box
      p={{ sm: '500' }}
      flexDirection={{ sm: 'column' }}
      style={{ borderBottom: `1px solid ${theme.colors.grey[300]}` }}
    >
      <Box justifyContent={{ sm: 'space-between' }}>
        <Text size="400" color="grey.1000" weight="600" family="graphikCompact">
          <Trans id={CommentMessages[timesheetReason].message} />
        </Text>
        <Icon
          fillColor="bechtel.blue.800"
          icon="edit"
          size={IconSize.LARGE}
          style={{ cursor: 'pointer' }}
          onClick={() => setIsEdit((prev) => !prev)}
        />
      </Box>
      <Box mt={{ sm: '300' }} gap={{ sm: '400' }}>
        <Text size="400" color="grey.600" family="graphikCompact">
          {hourDescription}
        </Text>
        <Text size="400" color="grey.600" family="graphikCompact">
          {timesheetEntry.costCodeId}
        </Text>
      </Box>
      {isEdit ? (
        <Box mt={{ sm: '600' }} flexDirection={{ sm: 'column' }}>
          <AddComment
            reason={get(timesheetEntry, 'commentId', '') as string}
            comment={get(timesheetEntry, 'otherCommentDescription', '') as string}
            setComment={(comment) => setFieldValue(`${index}.otherCommentDescription`, comment)}
            setReason={(reason) => setFieldValue(`${index}.commentId`, reason.id)}
          />
          {errors?.otherCommentDescription && (
            <Box mt={{ sm: '400' }}>
              <Text color="bechtel.red">
                <Trans>{errors.otherCommentDescription}</Trans>
              </Text>
            </Box>
          )}
        </Box>
      ) : timesheetEntry.otherCommentDescription && otherReasonId === timesheetEntry.commentId ? (
        <Box mt={{ sm: '700' }}>
          <Text size="300" color="grey.1000" family="graphik">
            {timesheetEntry.otherCommentDescription}
          </Text>
        </Box>
      ) : null}
    </Box>
  );
};

export const ViewCommentsSidesheet: FC<ViewCommentSidesheetProps> = ({
  crewNumber,
  timesheetCrewSummary,
  closeSidesheet,
  onSubmit,
}) => {
  const { getFullName } = useName();
  const { crewEmployee: crewMember, timeEntries } = timesheetCrewSummary;
  const filteredTimeEntries = timeEntries.filter((timeEntry) => !!timeEntry.commentId);
  const totalDailyHours = sumBy(timeEntries, (timeEntry) => (timeEntry.hours ? timeEntry.hours : 0));
  const { hourTypes, duration } = useTimesheet();
  const { data } = useTimeSheetReason();
  const reasonList = get(data, 'timesheetReasons', []);
  const otherReasonId = find(reasonList, (reason) => reason.name == TimesheetReasons.TIMESHEET_REASON_OTHER)?.id;
  const validationSchema = object().shape({
    commentId: string().required(t`Reason is mandatory`),
    otherCommentDescription: string().when('commentId', {
      is: (commentId: string) => commentId === otherReasonId,
      then: string()
        .required('Comment is required')
        .max(2000, t`Too Long!`),
      otherwise: string().max(2000, t`Too Long!`),
    }),
  });

  return (
    <SideSheet onDismiss={closeSidesheet} size={SideSheetSize.DEFAULT} visible>
      <SideSheetHeader title={t`Comments`}>
        <SideSheetClose onClick={closeSidesheet} size={ButtonSize.SMALL}></SideSheetClose>
      </SideSheetHeader>
      <Formik
        initialValues={filteredTimeEntries}
        validationSchema={validationSchema}
        onSubmit={(values: TimeEntryInput[]) => {
          const changeValues = differenceWith(values, filteredTimeEntries, isEqual);
          onSubmit(
            changeValues.map((item) => ({
              ...item,
              otherCommentDescription: item.commentId === otherReasonId ? item.otherCommentDescription : '',
            }))
          );
          closeSidesheet();
        }}
      >
        {({ isValid, dirty, errors, values, handleSubmit, setFieldValue }) => {
          return (
            <>
              <SideSheetBody>
                <Box flexDirection={{ sm: 'column' }} css={{ width: '100%' }}>
                  <Box
                    p={{ sm: '500' }}
                    flexDirection={{ sm: 'column' }}
                    css={`
                      background-color: ${theme.colors.bechtel.blue[100]};
                    `}
                  >
                    <Box justifyContent={{ sm: 'space-between' }} alignItems={{ sm: 'start' }}>
                      <Box flexDirection={{ sm: 'row' }} alignItems={{ sm: 'start' }} gap={{ sm: '400' }}>
                        <Box>
                          <AvatarRegularStyled
                            data-testid="avatar"
                            src={crewMember?.imageUrl}
                            hasImageUrl={!!crewMember?.imageUrl}
                          />
                        </Box>
                        <Box
                          flexDirection={{ sm: 'column' }}
                          justifyContent={{ sm: 'center' }}
                          style={{ gap: theme.spacings['100'] }}
                        >
                          <Text
                            size="400"
                            color="grey.1000"
                            weight="600"
                            family="graphikCompact"
                            style={{ lineHeight: 1.57 }}
                          >
                            {getFullName(crewMember.firstName, crewMember.lastName)}
                          </Text>
                          <Text size="300" color="grey.600" family="graphikCompact">
                            {[capitalize(crewMember.persona || ''), '-', crewNumber].join(' ')}
                          </Text>
                        </Box>
                      </Box>
                      <Box>
                        <Text size="900" color="grey.1000" family="graphikCompact">
                          {totalDailyHours}
                        </Text>
                        <Text size="300" color="grey.1000" family="graphikCompact" style={{ alignSelf: 'end' }}>
                          {duration ? `/${duration}` : ''}
                        </Text>
                      </Box>
                    </Box>
                    <Box mt={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
                      <Box gap={{ sm: '500' }}>
                        <TitleValue title={t`Time In`} value={'N/A'} />
                        <TitleValue title={t`Time Out`} value={'N/A'} />
                      </Box>
                      <Box gap={{ sm: '500' }} mt={{ sm: '500' }}>
                        {map(hourTypes, (hourType) => {
                          return (
                            <TitleValue
                              title={hourType.description ? hourType.description : hourType.hourCode}
                              value={`${sumBy(
                                filter(filteredTimeEntries, (te) => te.hourCode === hourType.hourCode),
                                (timeEntry) => (timeEntry.hours ? timeEntry.hours : 0)
                              ).toFixed(1)}`}
                            ></TitleValue>
                          );
                        })}
                      </Box>
                    </Box>
                  </Box>
                  <Box
                    pl={{ sm: '500' }}
                    pt={{ sm: '550' }}
                    pb={{ sm: '450' }}
                    css={`
                      background-color: ${theme.colors.grey[200]};
                    `}
                  >
                    <Text family={'graphik'} size="400" weight={'600'} color="grey.1000">
                      <Trans>Comments</Trans> ({filteredTimeEntries.length})
                    </Text>
                  </Box>
                  {reasonList.length ? (
                    <Box pb={{ sm: '900' }} flexDirection={{ sm: 'column' }}>
                      {map(values, (timesheetEntry, index) => (
                        <Comment
                          timesheetEntry={timesheetEntry}
                          index={index}
                          errors={errors[index]}
                          setFieldValue={setFieldValue}
                          reasonList={reasonList}
                        />
                      ))}
                    </Box>
                  ) : null}
                </Box>
              </SideSheetBody>
              <SideSheetFooter>
                <Box justifyContent={{ sm: 'flex-end' }}>
                  <Box mr={{ sm: '400' }}>
                    <FooterButtonStyled onClick={closeSidesheet}>
                      <Trans>Cancel</Trans>
                    </FooterButtonStyled>
                  </Box>
                  <FooterButtonStyled onClick={() => handleSubmit()} disabled={!dirty || !isValid}>
                    <Trans>Update Time</Trans>
                  </FooterButtonStyled>
                </Box>
              </SideSheetFooter>
            </>
          );
        }}
      </Formik>
    </SideSheet>
  );
};
